"use client";

import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import * as React from "react";
import { cn } from "./lib/utils";

const RadioGroup = React.forwardRef<
	React.ElementRef<typeof RadioGroupPrimitive.Root>,
	React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root> & {
		className?: string;
	}
>(({ className, ...props }, ref) => {
	return (
		<RadioGroupPrimitive.Root
			className={cn("flex flex-col gap-sm", className)}
			{...props}
			ref={ref}
		/>
	);
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

const RadioGroupItem = React.forwardRef<
	React.ElementRef<typeof RadioGroupPrimitive.Item>,
	React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> & {
		className?: string;
	}
>(({ className, children, ...props }, ref) => {
	return (
		<label className={cn("flex items-center gap-md cursor-pointer", className)}>
			<RadioGroupPrimitive.Item
				ref={ref}
				className={cn(
					"h-4 w-4 rounded-full border flex items-center justify-center flex-shrink-0",
					"data-[state=checked]:border-surface-brand data-[state=checked]:bg-surface-brand",
				)}
				{...props}
			>
				<RadioGroupPrimitive.Indicator className="flex items-center justify-center">
					<div className="h-2 w-2 rounded-full bg-white" />
				</RadioGroupPrimitive.Indicator>
			</RadioGroupPrimitive.Item>
			<span className="text-md">{children}</span>
		</label>
	);
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

// Higher-level component that accepts options array
interface RadioGroupWithOptionsProps
	extends Omit<
		React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>,
		"children"
	> {
	options: Array<{ value: string | number; label: string }>;
	className?: string;
}

const RadioGroupWithOptions = React.forwardRef<
	React.ElementRef<typeof RadioGroupPrimitive.Root>,
	RadioGroupWithOptionsProps
>(({ options, className, ...props }, ref) => {
	return (
		<RadioGroup ref={ref} className={className} {...props}>
			{options.map(({ value, label }) => (
				<RadioGroupItem key={value} value={value.toString()}>
					{label}
				</RadioGroupItem>
			))}
		</RadioGroup>
	);
});
RadioGroupWithOptions.displayName = "RadioGroupWithOptions";

const PillRadioGroupItem = React.forwardRef<
	React.ElementRef<typeof RadioGroupPrimitive.Item>,
	React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> & {
		className?: string;
	}
>(({ className, children, ...props }, ref) => {
	return (
		<RadioGroupPrimitive.Item
			ref={ref}
			className={cn(
				"px-4 inline-flex items-center justify-center h-8 rounded-full cursor-pointer transition-colors",
				"border border-white/10 hover:bg-white/5",
				"data-[state=checked]:bg-white/10 data-[state=checked]:border-white/20",
				className,
			)}
			{...props}
		>
			<span className="text-sm text-white/80">{children}</span>
		</RadioGroupPrimitive.Item>
	);
});
PillRadioGroupItem.displayName = "PillRadioGroupItem";

interface PillRadioGroupWithOptionsProps
	extends Omit<
		React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>,
		"children"
	> {
	options: Array<{ value: string | number; label: string }>;
	className?: string;
}

const PillRadioGroupWithOptions = React.forwardRef<
	React.ElementRef<typeof RadioGroupPrimitive.Root>,
	PillRadioGroupWithOptionsProps
>(({ options, className, ...props }, ref) => {
	return (
		<RadioGroupPrimitive.Root
			ref={ref}
			className={cn("flex flex-wrap gap-2", className)}
			{...props}
		>
			{options.map(({ value, label }) => (
				<PillRadioGroupItem key={value} value={value.toString()}>
					{label}
				</PillRadioGroupItem>
			))}
		</RadioGroupPrimitive.Root>
	);
});
PillRadioGroupWithOptions.displayName = "PillRadioGroupWithOptions";

export {
	PillRadioGroupItem,
	PillRadioGroupWithOptions,
	RadioGroup,
	RadioGroupItem,
	RadioGroupWithOptions,
};
